import {
    Intercom,
    boot,
    show,
    update,
    shutdown,
    hide,
} from '@intercom/messenger-js-sdk';
import { ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { switchMap, exhaustMap, withLatestFrom } from 'rxjs/operators';

import { storeLogout } from '@perpay-web/authentication/actions';
import { authentication } from '@perpay-web/authentication/singleton';
import { getIsComplete } from '@perpay-web/data-module/useDataModule';
import { STORE_REPLACE_AUTH } from '@perpay-web/fintech/constants/actionTypes';
import { INTERCOM_APP_ID } from '@perpay-web/fintech/constants/serviceCredentials';
import { DECAGON_LOADER_SCRIPT } from '@perpay-web/fintech/constants/urls';
import { getJWTData } from '@perpay-web/fintech/selectors/authentication';
import {
    fetchDecagonSignatureState$,
    getIsEnabled as getIsEnabledForDecagon,
    getSignature,
    getEpoch,
    getBorrowerTier,
    getHasProvisionedCard,
} from '@perpay-web/hooks/useFetchDecagonSignatureDataModuleContext';
import { nativeSetStatusBarAttributes } from '@perpay-web/native-client/actions/statusBar';
import { getIsReactNative } from '@perpay-web/native-client/getIsReactNative';
import { getAppVersion } from '@perpay-web/native-client/selectors/appInfo';
import { dispatchObservable } from '@perpay-web/observable/dispatchObservable';
import {
    loadDecagonSupportTool as loadDecagonSupportToolAction,
    fetchDecagonSignature as fetchDecagonSignatureAction,
    fetchDecagonSignatureSuccess,
} from '@perpay-web/services/decagon';
import {
    updateSupportTool as updateSupportToolAction,
    launchSupportTool as launchSupportToolAction,
    loadSupportTool as loadSupportToolAction,
    shutDownSupportTool as shutDownSupportToolAction,
} from '@perpay-web/services/supportTool';
import { isSemiLargeScreen } from '@perpay-web/utils/responsiveUtils';
import { loadThirdPartyScript } from '@perpay-web/utils/scriptUtils';

const getNativeAwareDecagonEnabled = (isEnabled, nativeAppVersion) =>
    isEnabled && (!getIsReactNative() || nativeAppVersion);

export const loadSupportTool = (action$, state$, { nativeState$ }) =>
    action$.pipe(
        ofType(STORE_REPLACE_AUTH),
        withLatestFrom(nativeState$),
        switchMap(([, nativeState]) =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: () => [fetchDecagonSignatureAction()],
                waitFor: [fetchDecagonSignatureSuccess().type],
                waitForDispatch: (currentState, results) => {
                    const { intercomHash, fullName, email } =
                        getJWTData(currentState);
                    const hideLauncher = !isSemiLargeScreen(window.innerWidth);
                    const nativeAppVersion = getAppVersion(nativeState);
                    const { payload } = results[0];
                    const { isEnabled } = payload;
                    const isEnabledForDecagon = getNativeAwareDecagonEnabled(
                        isEnabled,
                        nativeAppVersion,
                    );
                    if (isEnabledForDecagon) {
                        return [loadDecagonSupportToolAction({ hideLauncher })];
                    }

                    Intercom({
                        app_id: INTERCOM_APP_ID,
                        alignment: 'left',
                        hide_default_launcher: hideLauncher,
                    });
                    boot({
                        user_hash: intercomHash,
                        name: fullName,
                        email,
                    });
                    hide(); // show() called by default after initialization

                    return [loadSupportToolAction()];
                },
            }),
        ),
    );

export const launchSupportTool = (action$, state$, { nativeState$ }) =>
    action$.pipe(
        ofType(launchSupportToolAction.type),
        withLatestFrom(state$, fetchDecagonSignatureState$, nativeState$),
        switchMap(([, state, fetchDecagonSignatureState, nativeState]) => {
            const { intercomHash } = getJWTData(state);
            const nativeAppVersion = getAppVersion(nativeState);
            const isEnabled = getIsEnabledForDecagon(
                fetchDecagonSignatureState,
            );
            const isEnabledForDecagon = getNativeAwareDecagonEnabled(
                isEnabled,
                nativeAppVersion,
            );
            if (isEnabledForDecagon && window.duet) {
                window.duet.open();

                // This is the only way I could get the close button on the widget not
                // to render in the status bar
                // TODO: Apply styling in updateSupportTool instead of unhiding status bar
                return [nativeSetStatusBarAttributes({ hidden: false })];
            }
            show({
                app_id: INTERCOM_APP_ID,
                user_hash: intercomHash,
                hide_default_launcher: true,
            });

            return EMPTY;
        }),
    );

export const updateSupportTool = (action$, state$, { nativeState$ }) =>
    action$.pipe(
        ofType(updateSupportToolAction.type),
        withLatestFrom(fetchDecagonSignatureState$, nativeState$),
        switchMap(([action, fetchDecagonSignatureState, nativeState]) => {
            const isDecagonComplete = getIsComplete(fetchDecagonSignatureState);
            if (!isDecagonComplete) {
                return EMPTY;
            }
            const isEnabled = getIsEnabledForDecagon(
                fetchDecagonSignatureState,
            );
            const nativeAppVersion = getAppVersion(nativeState);
            const isEnabledForDecagon = getNativeAwareDecagonEnabled(
                isEnabled,
                nativeAppVersion,
            );
            const {
                hide_default_launcher: hideLauncher,
                vertical_padding: verticalPadding,
            } = action.payload;

            if (isEnabledForDecagon && window.duet) {
                if (hideLauncher) {
                    window.duet.hide();
                } else {
                    window.duet.unhide();
                }

                if (verticalPadding) {
                    window.duet.updateStyle({
                        bottom: `${verticalPadding - 20}px`,
                    });
                }
            } else if (authentication.getIsAuthenticated()) {
                Intercom({
                    app_id: INTERCOM_APP_ID,
                    alignment: 'left',
                });
                update({ app_id: INTERCOM_APP_ID, ...action.payload });
            }
            return EMPTY;
        }),
    );

export const shutDownSupportTool = (action$, state$) =>
    action$.pipe(
        ofType(storeLogout().type),
        withLatestFrom(state$),
        switchMap(() => {
            if (window.duet) {
                window.duet.hide();
            }
            shutdown();
            return [shutDownSupportToolAction()];
        }),
    );

export const loadDecagonSupportTool = (action$, state$) =>
    action$.pipe(
        ofType(loadDecagonSupportToolAction().type),
        withLatestFrom(state$, fetchDecagonSignatureState$),
        exhaustMap(([action, state, fetchDecagonSignatureState]) => {
            const { intercomHash, fullName, firstName, lastName, email, uuid } =
                getJWTData(state);

            const signature = getSignature(fetchDecagonSignatureState);
            const epoch = getEpoch(fetchDecagonSignatureState);
            const borrowerTier = getBorrowerTier(fetchDecagonSignatureState);
            const hasProvisionedCard = getHasProvisionedCard(
                fetchDecagonSignatureState,
            );
            shutdown();
            const { hideLauncher } = action.payload;
            loadThirdPartyScript(DECAGON_LOADER_SCRIPT)
                .then(() => {
                    window.duet.setWidgetCornerPosition('bottom-left');
                    window.duet.setUserId(uuid);
                    window.duet.setUserIdAuth(signature, epoch);
                    window.duet.setMetadata({
                        firstName,
                        lastName,
                        email,
                        borrowerTier,
                        hasProvisionedCard,
                    });
                    if (hideLauncher) {
                        window.duet.hide();
                        window.duet.setWidgetCornerPosition('bottom-right');
                    } else {
                        window.duet.unhide();
                    }
                })
                .catch(() => {
                    // Fallback to Intercom
                    Intercom({
                        app_id: INTERCOM_APP_ID,
                        alignment: 'left',
                        hide_default_launcher: hideLauncher,
                    });
                    boot({
                        user_hash: intercomHash,
                        name: fullName,
                        email,
                    });
                    hide();
                });
            return [loadSupportToolAction()];
        }),
    );
